import React, { useEffect, useState } from "react";
import axios from "axios";
import HiddenLOCOReport from "./HiddenLOCOReport";
import SurveyInviteWidget from "./SurveyInviteWidget";

// ─── SCALE DEFINITIONS ────────────────────────────────────────────────────────
// apiKey  — key used in the API response scores object (snake_case)
// code    — short code used internally and in HiddenLOCOReport
const LOCO_SCALES = [
  { code: "I",  apiKey: "internal",        label: "Internal"        },
  { code: "EO", apiKey: "external_others", label: "External-Others" },
  { code: "EC", apiKey: "external_chance", label: "External-Chance" },
];

// ─── Band → colour map (high / moderate / low) ────────────────────────────────
const bandColor = (band) => {
  if (!band) return { bg: "#f3f4f6", color: "#6b7280", border: "#d1d5db" };
  switch (band.toLowerCase()) {
    case "high":     return { bg: "#dcfce7", color: "#1f8f55", border: "#bbf7d0" }; // green
    case "moderate": return { bg: "#fef3c7", color: "#d88a1f", border: "#fde68a" }; // amber
    case "low":      return { bg: "#fee2e2", color: "#b91c1c", border: "#fecaca" }; // red
    default:         return { bg: "#f3f4f6", color: "#6b7280", border: "#d1d5db" };
  }
};

// ─── Scale code → badge colour ────────────────────────────────────────────────
const scaleColor = (code) => {
  switch ((code || "").toUpperCase()) {
    case "I":  return { bg: "#dbeafe", color: "#1d4ed8", border: "#bfdbfe" }; // blue
    case "EO": return { bg: "#dcfce7", color: "#1f8f55", border: "#bbf7d0" }; // green
    case "EC": return { bg: "#fef3c7", color: "#b45309", border: "#fde68a" }; // amber
    default:   return { bg: "#f3f4f6", color: "#6b7280", border: "#d1d5db" };
  }
};

// ─── Scale descriptions (tooltip) ─────────────────────────────────────────────
const SCALE_DESC = {
  I:  "Internal: believes personal effort, decisions, and competence primarily drive outcomes.",
  EO: "External-Others: believes powerful people and authority figures significantly shape outcomes.",
  EC: "External-Chance: believes luck, fate, and unpredictable circumstances are major drivers of outcomes.",
};

// ─── Pretty label for a short code ────────────────────────────────────────────
const scaleLabelFromCode = (code = "") => {
  switch (code.toUpperCase()) {
    case "I":  return "Internal";
    case "EO": return "External-Others";
    case "EC": return "External-Chance";
    default:   return code;
  }
};

// ─── Extract flat { I, EO, EC } score map from API scores object ──────────────
// API shape: scores.internal.score / scores.external_others.score / scores.external_chance.score
const extractFlatScores = (scoresObj = {}) => {
  const flat = {};
  LOCO_SCALES.forEach(({ code, apiKey }) => {
    flat[code] = scoresObj[apiKey]?.score ?? null;
  });
  return flat;
};

// ─── Derive dominant code(s) — scale(s) with the highest score ────────────────
// Returns an array so ties (e.g. EO=38 & EC=38) are both shown
const getDominantCodes = (scoresObj = {}) => {
  const entries = LOCO_SCALES.map(({ code, apiKey }) => ({
    code,
    score: scoresObj[apiKey]?.score ?? 0,
  }));
  const max = Math.max(...entries.map((e) => e.score));
  if (max === 0) return [];
  return entries.filter((e) => e.score === max).map((e) => e.code);
};

// =============================================================================
export default function GlobalLOCOResponses({ surveyId }) {
  const [locoData,        setLocoData]        = useState([]);
  const [loading,         setLoading]         = useState(true);

  // ── Filters ──
  const [filterName,      setFilterName]      = useState("");
  const [filterEmail,     setFilterEmail]     = useState("");
  const [filterCompany,   setFilterCompany]   = useState("");
  const [filterDept,      setFilterDept]      = useState("");
  const [filterDominant,  setFilterDominant]  = useState("");
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate,   setFilterEndDate]   = useState("");

  // ── PDF state ──
  const [downloadData,    setDownloadData]    = useState(null);
  const [isGeneratingRow, setIsGeneratingRow] = useState(null);

  // ================= FETCH =================
  useEffect(() => {
    if (surveyId) fetchLOCOResults();
  }, [surveyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchLOCOResults = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/response/calculate-score/loco/all`,
        { params: { survey_id: surveyId } }
      );
      setLocoData(res.data.data || []);
    } catch (err) {
      console.error("❌ Failed to fetch LOCO results", err);
    } finally {
      setLoading(false);
    }
  };

  // ================= FILTER =================
  // Collect all unique dominant codes across all respondents (for dropdown)
  const uniqueDominantCodes = [
    ...new Set(
      locoData.flatMap((u) => getDominantCodes(u.scores || {}))
    ),
  ].sort();

  const uniqueCompanies = [
    ...new Set(locoData.map((u) => (u?.company || "").trim()).filter(Boolean)),
  ].sort();

  const uniqueDepts = [
    ...new Set(locoData.map((u) => (u?.department || "").trim()).filter(Boolean)),
  ].sort();

  const filteredData = locoData.filter((user) => {
    const name          = (user?.name  || "").toLowerCase();
    const email         = (user?.email || "").toLowerCase();
    const company       = (user?.company || "").trim().toLowerCase();
    const dept          = (user?.department || "").trim().toLowerCase();
    const dominantCodes = getDominantCodes(user.scores || {});

    const matchName     = name.includes(filterName.toLowerCase());
    const matchEmail    = email.includes(filterEmail.toLowerCase());
    const matchCompany  = filterCompany === "" || company === filterCompany.toLowerCase();
    const matchDept     = filterDept    === "" || dept    === filterDept.toLowerCase();
    const matchDominant =
      filterDominant === "" || dominantCodes.includes(filterDominant.toUpperCase());

    let matchDate = true;
    if (filterStartDate || filterEndDate) {
      const submissionDate = new Date(user.submission_time);
      submissionDate.setHours(0, 0, 0, 0);
      if (filterStartDate) {
        const start = new Date(filterStartDate);
        start.setHours(0, 0, 0, 0);
        if (submissionDate < start) matchDate = false;
      }
      if (filterEndDate) {
        const end = new Date(filterEndDate);
        end.setHours(23, 59, 59, 999);
        if (submissionDate > end) matchDate = false;
      }
    }

    return matchName && matchEmail && matchCompany && matchDept && matchDominant && matchDate;
  });

  const clearFilters = () => {
    setFilterName("");
    setFilterEmail("");
    setFilterCompany("");
    setFilterDept("");
    setFilterDominant("");
    setFilterStartDate("");
    setFilterEndDate("");
  };

  // ── Summary counts — tally each dominant code across all respondents ──────────
  const dominantCounts = locoData.reduce((acc, u) => {
    getDominantCodes(u.scores || {}).forEach((code) => {
      acc[code] = (acc[code] || 0) + 1;
    });
    return acc;
  }, {});

  // ================= PDF DOWNLOAD =================
  const handleDownloadPDF = (user, rowIndex) => {
    setIsGeneratingRow(rowIndex);

    const sc           = user.scores || {};
    const flatScores   = extractFlatScores(sc);
    const dominantArr  = getDominantCodes(sc);
    // HiddenLOCOReport expects a single dominantCode; use the highest scoring one
    const dominantCode = dominantArr[0] ?? null;

    setDownloadData({
      userInfo: {
        name:  user?.name  || "Participant",
        email: user?.email || "",
        phone: user?.phone || "",
      },
      locoData: { scores: sc, profile: user.profile ?? null },         // full scores object for reference in HiddenLOCOReport
      scores:      flatScores,   // flat { I, EO, EC } map consumed by HiddenLOCOReport
      dominantCode,              // e.g. "I", "EO", or "EC"
      insightText: user?.profile?.report_text || "",
      submission_time: user.submission_time,
    });
  };

  const handleDownloadComplete = () => {
    setDownloadData(null);
    setIsGeneratingRow(null);
  };

  // ================= UI =================
  if (loading)
    return <div className="loading-state">Loading Locus of Control data...</div>;

  return (
    <div style={{ marginTop: "20px" }}>
       <SurveyInviteWidget surveyId={surveyId} />

      {/* Hidden PDF renderer */}
      <HiddenLOCOReport
        participantData={downloadData}
        onComplete={handleDownloadComplete}
      />

      {/* ── Header ── */}
      <div style={{
        display: "flex", justifyContent: "space-between",
        alignItems: "center", marginBottom: "15px",
      }}>
        <h3 style={{ margin: 0 }}>🎯 Locus of Control Responses ({filteredData.length})</h3>
        <span style={{
          background: "#A81026", color: "white",
          padding: "5px 10px", borderRadius: "4px",
          fontSize: "12px", fontWeight: "bold",
        }}>
          Global Public View
        </span>
      </div>

      {/* ── Dominant-scale summary pills (clickable filter) ── */}
      {locoData.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "16px" }}>
          {Object.entries(dominantCounts).map(([code, count]) => {
            const sc2      = scaleColor(code);
            const label    = scaleLabelFromCode(code);
            const isActive = filterDominant === code;
            return (
              <span
                key={code}
                onClick={() => setFilterDominant(isActive ? "" : code)}
                title={SCALE_DESC[code] || `Click to filter by ${label}`}
                style={{
                  background:    sc2.bg,
                  color:         sc2.color,
                  border:        `1px solid ${sc2.border}`,
                  padding:       "4px 12px",
                  borderRadius:  "999px",
                  fontSize:      "12px",
                  fontWeight:    "700",
                  cursor:        "pointer",
                  outline:       isActive ? `2px solid ${sc2.color}` : "none",
                  outlineOffset: "2px",
                  userSelect:    "none",
                }}
              >
                {label}: {count}
              </span>
            );
          })}
          <span style={{ fontSize: "12px", color: "#888", alignSelf: "center", marginLeft: "4px" }}>
            ← click to filter
          </span>
        </div>
      )}

      {/* ── Filter Bar ── */}
      <div style={{
        display: "flex", flexWrap: "wrap", gap: "10px",
        background: "#f8f9fa", padding: "15px", borderRadius: "8px",
        border: "1px solid #ddd", marginBottom: "20px", alignItems: "center",
      }}>

        <div style={{ flex: "1", minWidth: "150px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Name</label>
          <input
            type="text"
            placeholder="Search name..."
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
          />
        </div>

        <div style={{ flex: "1", minWidth: "150px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Email</label>
          <input
            type="text"
            placeholder="Search email..."
            value={filterEmail}
            onChange={(e) => setFilterEmail(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
          />
        </div>

        <div style={{ flex: "1", minWidth: "160px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Company</label>
          <select
            value={filterCompany}
            onChange={(e) => setFilterCompany(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc", background: "#fff" }}
          >
            <option value="">All Companies</option>
            {uniqueCompanies.map(c => (
              <option key={c} value={c.toLowerCase()}>{c}</option>
            ))}
          </select>
        </div>

        <div style={{ flex: "1", minWidth: "160px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Department</label>
          <select
            value={filterDept}
            onChange={(e) => setFilterDept(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc", background: "#fff" }}
          >
            <option value="">All Departments</option>
            {uniqueDepts.map(d => (
              <option key={d} value={d.toLowerCase()}>{d}</option>
            ))}
          </select>
        </div>

        <div style={{ flex: "1", minWidth: "160px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Dominant Scale</label>
          <select
            value={filterDominant}
            onChange={(e) => setFilterDominant(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc", background: "#fff" }}
          >
            <option value="">All Scales</option>
            {uniqueDominantCodes.map((code) => (
              <option key={code} value={code}>
                {scaleLabelFromCode(code)} ({code})
              </option>
            ))}
          </select>
        </div>

        <div style={{ flex: "1", minWidth: "130px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Start Date</label>
          <input
            type="date"
            value={filterStartDate}
            onChange={(e) => setFilterStartDate(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
          />
        </div>

        <div style={{ flex: "1", minWidth: "130px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>End Date</label>
          <input
            type="date"
            value={filterEndDate}
            onChange={(e) => setFilterEndDate(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
          />
        </div>

        <div style={{ marginTop: "20px" }}>
          <button
            onClick={clearFilters}
            style={{
              padding: "8px 15px", background: "#A81026", color: "white",
              border: "none", borderRadius: "4px", cursor: "pointer", fontWeight: "bold",
            }}
          >
            Clear Filters
          </button>
        </div>

      </div>

      {/* ── Table ── */}
      {locoData.length === 0 ? (
        <div style={{ padding: "20px", textAlign: "center", color: "#888" }}>
          No Locus of Control responses found.
        </div>
      ) : filteredData.length === 0 ? (
        <div style={{ padding: "20px", textAlign: "center", color: "#888" }}>
          No responses match your current filters.
        </div>
      ) : (
        <div style={{ overflowX: "auto", border: "1px solid #ddd", borderRadius: "8px", background: "#fff" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
            <thead>
              <tr style={{ background: "#f8f9fa", borderBottom: "2px solid #ddd" }}>
                <th style={{ padding: "12px" }}>Date</th>
                <th style={{ padding: "12px" }}>Participant</th>
                <th style={{ padding: "12px" }}>Company / Dept</th>
                <th style={{ padding: "12px", minWidth: "280px" }}>Scale Scores</th>
                <th style={{ padding: "12px", minWidth: "160px", background: "#fdf6e3" }}>
                  🎯 Dominant Scale
                </th>
                <th style={{ padding: "12px", minWidth: "180px" }}>Profile</th>
                <th style={{ padding: "12px", textAlign: "center" }}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {filteredData.map((user, index) => {
                const sc            = user.scores  || {};
                const profile       = user.profile || {};
                const dominantCodes = getDominantCodes(sc);
                const isGenerating  = isGeneratingRow === index;

                return (
                  <tr key={index} style={{ borderBottom: "1px solid #eee" }}>

                    {/* ── Date ── */}
                    <td style={{ padding: "12px", whiteSpace: "nowrap" }}>
                      {user.submission_time
                        ? <>
                            {new Date(user.submission_time).toLocaleDateString("en-US", {
                              month: "short", day: "numeric", year: "numeric",
                            })}
                            <br />
                            <span style={{ fontSize: "12px", color: "#888" }}>
                              {new Date(user.submission_time).toLocaleTimeString("en-US", {
                                hour: "2-digit", minute: "2-digit",
                              })}
                            </span>
                          </>
                        : <span style={{ color: "#aaa", fontStyle: "italic" }}>—</span>
                      }
                    </td>

                    {/* ── Participant ── */}
                    <td style={{ padding: "12px" }}>
                      <b>{user?.name || "Unknown"}</b><br />
                      <span style={{ fontSize: "13px", color: "#555" }}>
                        {user?.email}<br />
                        {user?.phone || ""}
                      </span>
                    </td>

                    {/* ── Company / Dept ── */}
                    <td style={{ padding: "12px" }}>
                      {user?.company
                        ? <><b>{user.company}</b><br /></>
                        : <span style={{ color: "#aaa", fontStyle: "italic" }}>N/A</span>
                      }
                      {user?.department
                        ? <span style={{ fontSize: "13px", color: "#555" }}>{user.department}</span>
                        : null
                      }
                    </td>

                    {/* ── Scale score pills: reads from apiKey (internal / external_others / external_chance) ── */}
                    <td style={{ padding: "12px" }}>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                        {LOCO_SCALES.map(({ code, apiKey, label }) => {
                          const dim       = sc[apiKey] || {};
                          const score     = dim.score      ?? null;
                          const band      = dim.band       ?? null;
                          const bandLabel = dim.band_label ?? band ?? null;
                          const c         = bandColor(band);
                          return (
                            <span
                              key={code}
                              title={`${label}: ${score ?? "—"} pts · Band: ${bandLabel ?? "—"} · ${SCALE_DESC[code]}`}
                              style={{
                                background:   c.bg,
                                color:        c.color,
                                border:       `1px solid ${c.border}`,
                                padding:      "2px 8px",
                                borderRadius: "4px",
                                fontSize:     "12px",
                                fontWeight:   "700",
                                whiteSpace:   "nowrap",
                              }}
                            >
                              {code}: {score ?? "—"}
                              {bandLabel && (
                                <span style={{ fontWeight: "500", marginLeft: "4px", opacity: 0.8 }}>
                                  ({bandLabel})
                                </span>
                              )}
                            </span>
                          );
                        })}
                      </div>
                    </td>

                    {/* ── Dominant Scale badge(s) — supports ties ── */}
                    <td style={{ padding: "12px", background: "#fffdf7" }}>
                      {dominantCodes.length > 0 ? (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                          {dominantCodes.map((code) => {
                            const sc2 = scaleColor(code);
                            return (
                              <span
                                key={code}
                                title={SCALE_DESC[code] || ""}
                                style={{
                                  background:   sc2.bg,
                                  color:        sc2.color,
                                  border:       `1px solid ${sc2.border}`,
                                  padding:      "4px 10px",
                                  borderRadius: "999px",
                                  fontSize:     "12px",
                                  fontWeight:   "800",
                                  whiteSpace:   "nowrap",
                                  display:      "inline-block",
                                }}
                              >
                                {scaleLabelFromCode(code)}
                                <span style={{ marginLeft: 5, fontWeight: "500", opacity: 0.75 }}>
                                  ({code})
                                </span>
                              </span>
                            );
                          })}
                        </div>
                      ) : (
                        <span style={{ color: "#aaa", fontStyle: "italic" }}>—</span>
                      )}
                    </td>

                    {/* ── Profile ── */}
                    <td style={{ padding: "12px", maxWidth: "200px" }}>
                      {profile.id ? (
                        <>
                          <span style={{
                            display: "inline-block",
                            background: "#fce7ec", color: "#A80D2C",
                            border: "1px solid #f4a0b0",
                            borderRadius: "4px", padding: "1px 7px",
                            fontSize: "11px", fontWeight: "700", marginBottom: "4px",
                          }}>
                           {profile.label || profile.dominance_pattern || ""}
                          </span>
                          <br />
                          {/* <span style={{ fontSize: "12px", color: "#555", lineHeight: "1.4" }}>
                            {profile.label || profile.dominance_pattern || ""}
                          </span> */}
                        </>
                      ) : (
                        <span style={{ color: "#aaa", fontStyle: "italic" }}>—</span>
                      )}
                    </td>

                    {/* ── Download ── */}
                    <td style={{ padding: "12px", textAlign: "center" }}>
                      <button
                        onClick={() => handleDownloadPDF(user, index)}
                        disabled={isGenerating}
                        style={{
                          background:   isGenerating ? "#95a5a6" : "#A81026",
                          color:        "white",
                          border:       "none",
                          padding:      "8px 12px",
                          borderRadius: "6px",
                          cursor:       isGenerating ? "not-allowed" : "pointer",
                          fontWeight:   "bold",
                          fontSize:     "12px",
                          display:      "flex",
                          alignItems:   "center",
                          gap:          "5px",
                          margin:       "0 auto",
                          transition:   "background 0.3s",
                          whiteSpace:   "nowrap",
                        }}
                      >
                        {isGenerating ? "⏳ Generating..." : "📄 Download"}
                      </button>
                    </td>

                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

    </div>
  );
}